import { z } from "zod";

import { VersionStringSchema } from "../manifest/schemas";
import { ModuleInstanceKeySchema } from "../module/instance-key";
import { RedirectResponseSchema } from "../response/schemas";
import { requiredUnknown } from "../schema-utils";
import type {
  ActionErrorCode,
  ActionErrorResponse,
  ActionHandlerErrorResponse,
  ActionPayload,
  ActionResponse,
  ActionSuccessResponse,
  ActionValidationErrorResponse,
  BaseActionPayload,
  ModuleActionPayload,
  ViewInteractionActionPayload,
} from "./types";

// ============================================================================
// Error codes
// ============================================================================

/**
 * Zod enum for {@link ActionErrorCode}.
 */
export const ActionErrorCodeSchema = z.enum([
  "no_flow_match",
  "module_load_failed",
  "action_not_found",
  "interaction_not_found",
  "auth_failed",
  "internal_error",
  "view_required",
  "route_view_mismatch",
  "module_instance_invalid",
  "data_params_invalid",
  "module_not_in_view",
  "module_version_mismatch",
  "view_load_failed",
  "add_response_headers_failed",
]) satisfies z.ZodType<ActionErrorCode>;

// ============================================================================
// Validation errors
// ============================================================================

/**
 * Schema that normalises validation-error values into
 * `Record<string, Array<string>>`.
 *
 * Accepts either `string` or `Array<string>` values (and coerces entries to
 * strings). Keys whose values are neither a string nor a string array are
 * silently dropped, matching the server's lenient contract.
 */
export const ValidationErrorsSchema = z
  .record(z.unknown())
  .transform((raw): Record<string, Array<string>> => {
    const out: Record<string, Array<string>> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (Array.isArray(value)) {
        out[key] = value.map((v) => String(v));
      } else if (typeof value === "string") {
        out[key] = [value];
      }
    }
    return out;
  });

// ============================================================================
// Response variants
// ============================================================================

/**
 * `data` uses {@link requiredUnknown} so Zod does not misinfer the field as
 * optional.
 */
export const ActionSuccessResponseSchema = z.object({
  type: z.literal("success"),
  data: requiredUnknown(),
  shouldRevalidate: z.boolean(),
}) satisfies z.ZodType<ActionSuccessResponse>;

/**
 * {@link ValidationErrorsSchema} normalises arbitrary record values into
 * `Record<string, Array<string>>`, so the schema's input type (a record of
 * `unknown`) diverges from its output type. We pin the `Input` generic of
 * `ZodType` to `unknown` so `satisfies` still enforces the output contract
 * without trying to match the transform input against the authored type.
 */
export const ActionValidationErrorResponseSchema = z.object({
  type: z.literal("validation_error"),
  message: z.string(),
  errors: ValidationErrorsSchema,
}) satisfies z.ZodType<ActionValidationErrorResponse, z.ZodTypeDef, unknown>;

export const ActionHandlerErrorResponseSchema = z.object({
  type: z.literal("handler_error"),
  message: z.string(),
  code: z.string().optional(),
}) satisfies z.ZodType<ActionHandlerErrorResponse>;

export const ActionErrorResponseSchema = z.object({
  type: z.literal("error"),
  status: z.number(),
  message: z.string(),
  code: ActionErrorCodeSchema,
}) satisfies z.ZodType<ActionErrorResponse>;

/**
 * Discriminated union over every action response shape emitted by
 * `handleAction` on the server. The `Input` generic is pinned to `unknown`
 * because one branch ({@link ActionValidationErrorResponseSchema}) has a
 * transform whose input differs from its output.
 */
export const ActionRedirectResponseSchema = RedirectResponseSchema;

export const ActionResponseSchema = z.discriminatedUnion("type", [
  ActionSuccessResponseSchema,
  ActionValidationErrorResponseSchema,
  ActionHandlerErrorResponseSchema,
  ActionErrorResponseSchema,
  ActionRedirectResponseSchema,
]) satisfies z.ZodType<ActionResponse, z.ZodTypeDef, unknown>;

// ============================================================================
// Request payload
// ============================================================================

/**
 * Same-origin path the action fired from (`pathname + search`). Rejects
 * absolute URLs and protocol-relative paths (`//…`) that would change origin
 * when resolved against the action request URL.
 */
export const ActionRouteSchema = z
  .string()
  .min(1)
  .refine(
    (route) => route.startsWith("/") && !route.startsWith("//"),
    "route must be a same-origin path starting with /",
  );

/**
 * Schema for {@link BaseActionPayload} — the fields shared by every variant
 * of {@link ActionPayload}.
 *
 * Lives as a standalone schema so the per-variant schemas below can extend
 * it via `.extend({ ... })`, mirroring the type-level inheritance and
 * keeping the shared field list in one place. `data` uses
 * {@link requiredUnknown} so Zod does not misinfer the field as optional.
 *
 * Not exported — outside callers should validate against
 * {@link ActionPayloadSchema} (or the per-variant schemas) so the required
 * `scope` discriminant is enforced.
 */
const BaseActionPayloadSchema = z.object({
  viewId: z.string().min(1),
  viewVersion: VersionStringSchema,
  actionName: z.string().min(1),
  data: requiredUnknown(),
  route: ActionRouteSchema,
}) satisfies z.ZodType<BaseActionPayload>;

/**
 * Schema for {@link ModuleActionPayload}.
 */
export const ModuleActionPayloadSchema = BaseActionPayloadSchema.extend({
  scope: z.literal("module"),
  moduleId: z.string().min(1),
  moduleVersion: VersionStringSchema,
  instanceKey: ModuleInstanceKeySchema.optional(),
}) satisfies z.ZodType<ModuleActionPayload>;

/**
 * Schema for {@link ViewInteractionActionPayload}.
 */
export const ViewInteractionActionPayloadSchema = BaseActionPayloadSchema.extend(
  {
    scope: z.literal("view_interaction"),
  },
) satisfies z.ZodType<ViewInteractionActionPayload>;

/**
 * Discriminated union of every {@link ActionPayload} variant accepted by the
 * `/action` endpoint, keyed on the required `scope` field.
 */
export const ActionPayloadSchema = z.discriminatedUnion("scope", [
  ModuleActionPayloadSchema,
  ViewInteractionActionPayloadSchema,
]) satisfies z.ZodType<ActionPayload>;
